import { Component, createSignal, onMount, Show } from "solid-js"

// Assets
import actorIcon from "./assets/agents/person/icons/actor.svg?raw"
import analysisIcon from "./assets/agents/person/icons/analysis.svg?raw"
import eventIcon from "./assets/agents/person/icons/event.svg?raw"
import pinIcon from "./assets/agents/person/icons/pin.svg?raw"
import settingsIcon from "./assets/agents/person/icons/settings.svg?raw"
import speedIcon from "./assets/agents/person/icons/speed.svg?raw"
import defaultScenario from "./data/scenario/default-scenario.json"

// Utils and stores
import { pauseAnimation, resetAnimation } from "./lib/animation"
import { NAMED_SCENARIOS } from "./lib/named-scenarios"
import { initPWAInstall } from "./lib/pwa-install"
import { loadDefaultScenario, loadScenarioFromProject } from "./lib/scenario-loader"
import { registerServiceWorker } from "./lib/service-worker"
import { getUserUUID } from "./lib/uuid"
import { handleViewTransition } from "./lib/view-transition"
import { useAppStore } from "./stores/app"
import { loadMapData } from "./stores/map"
import { useScenarioStore } from "./stores/scenario"
import type { AppMode, ClockAnnotation, Project, ScenarioMode } from "./types"

// Components
import AgentDetailsModal from "./components/AgentDetailsModal"
import AgentPanel from "./components/AgentPanel"
import AnalysisModal from "./components/AnalysisModal"
import AnalysisPanel from "./components/AnalysisPanel"
import ClockControls from "./components/ClockControls"
import CookieConsent from "./components/CookieConsent"
import EventsPanel from "./components/EventsPanel"
import HelpModal from "./components/HelpModal"
import MapView from "./components/MapView"
import MobileMenu from "./components/MobileMenu"
import MobileMessageBanner from "./components/MobileMessageBanner"
import RestartModal from "./components/RestartModal"
import SaveBeforeLoadModal from "./components/SaveBeforeLoadModal"
import SettingsPanel from "./components/SettingsPanel"
import ShareResultModal from "./components/ShareResultModal"
import SimulationOverlay from "./components/SimulationOverlay"
import SpeedPanel from "./components/SpeedPanel"
import Toolbar from "./components/Toolbar"
import ViewportPanel from "./components/ViewportPanel"
import WelcomeModal from "./components/WelcomeModal"

const App: Component = () => {
    // Get scenario store for persistence and loading
    const [scenario, scenarioActions] = useScenarioStore()
    const [app, appActions] = useAppStore()

    // Local state - not suitable for the global app store
    const [visibleAgentIds, setVisibleAgentIds] = createSignal<Set<string>>(new Set())
    const [selectedAgentForModal, setSelectedAgentForModal] = createSignal<any>(null)
    const [currentAnnotation, setCurrentAnnotation] = createSignal<ClockAnnotation | null>(null)
    const [pendingProject, setPendingProject] = createSignal<Project | null>(null)
    const [pendingNamedScenario, setPendingNamedScenario] = createSignal<any>(null)
    const [restartModalOpen, setRestartModalOpen] = createSignal(false)
    // When true, the save-before-load flow opens RestartModal instead of loading pendingProject
    const [pendingRestartAction, setPendingRestartAction] = createSignal(false)

    const handleEditTabChange = (tab: "agents" | "events" | "speed" | "viewport" | "analysis" | "settings") => {
        handleViewTransition(() => appActions.setEditTab(tab))
    }

    const loadPendingScenario = (project: Project) => {
        // Load the scenario
        loadScenarioFromProject(scenarioActions, project)
        resetAnimation()

        // Not owner - read-only until cloned
        appActions.setIsEditable(false)
        appActions.setScenarioMode("custom")
    }

    const loadPendingNamedScenario = (templateData: any) => {
        loadDefaultScenario(scenarioActions, templateData)
        resetAnimation()
        appActions.setIsEditable(false)
        appActions.setMode("simulate")
        appActions.setScenarioMode("default")
    }

    const handleSaveAndLoad = async () => {
        // Share current scenario first
        try {
            const userUUID = getUserUUID()
            const baseUrl = (
                import.meta.env.VITE_BASEURL_API || "https://sim86-worker.mycolorpicker.workers.dev"
            ).replace(/\/$/, "") // Remove trailing slash

            const now = new Date().toISOString()
            const projectToShare: Project = {
                meta: {
                    ...scenario.meta,
                    ownerUUID: userUUID,
                    createdAt: scenario.meta.createdAt || now,
                    updatedAt: now,
                },
                agents: JSON.parse(JSON.stringify(scenario.agents)),
                clockAnnotations: JSON.parse(JSON.stringify(scenario.clockAnnotations)),
                speedControls: JSON.parse(JSON.stringify(scenario.speedControls)),
                analysis: JSON.parse(JSON.stringify(scenario.analysis)),
                viewportPins: JSON.parse(JSON.stringify(scenario.viewportPins)),
                autoPan: scenario.autoPan,
            }

            const response = await fetch(`${baseUrl}/scenarios`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(projectToShare),
            })

            if (!response.ok) {
                throw new Error(`Failed to share: ${response.statusText}`)
            }

            const { slug } = await response.json()
            const url = `${window.location.origin}/${slug}`

            // Show share result
            appActions.setShareUrl(url)

            // Copy to clipboard (may fail in Safari after async operations)
            try {
                await navigator.clipboard.writeText(url)
            } catch (clipboardErr) {
                console.warn("Failed to copy to clipboard (Safari restriction):", clipboardErr)
                // Still show modal so user can manually copy
            }

            appActions.openModal("shareResult")

            // Then load the pending scenario
            const pending = pendingProject()
            const namedScenario = pendingNamedScenario()
            if (pending) {
                loadPendingScenario(pending)
            } else if (namedScenario) {
                loadPendingNamedScenario(namedScenario)
            } else if (pendingRestartAction()) {
                setRestartModalOpen(true)
            }
        } catch (err) {
            console.error("Failed to share scenario before loading new one:", err)
            if (err instanceof Error) {
                console.error("Error details:", {
                    message: err.message,
                    stack: err.stack,
                })
            }
            appActions.setErrorMessage("Det gick inte att dela scenariot. Försök igen.")
            appActions.openModal("error")
        } finally {
            appActions.closeModal("saveBeforeLoad")
            setPendingProject(null)
            setPendingNamedScenario(null)
            setPendingRestartAction(false)
        }
    }

    const handleDiscardAndLoad = () => {
        const pending = pendingProject()
        const namedScenario = pendingNamedScenario()
        if (pending) {
            loadPendingScenario(pending)
        } else if (namedScenario) {
            loadPendingNamedScenario(namedScenario)
        } else if (pendingRestartAction()) {
            setRestartModalOpen(true)
        }
        appActions.closeModal("saveBeforeLoad")
        setPendingProject(null)
        setPendingNamedScenario(null)
        setPendingRestartAction(false)
    }

    const handleCancelLoad = () => {
        appActions.closeModal("saveBeforeLoad")
        setPendingProject(null)
        setPendingNamedScenario(null)
        setPendingRestartAction(false)
        // Clear the slug from URL since we're not loading it
        window.history.replaceState({}, "", "/")

        // Restore localStorage state
        const loaded = scenarioActions.loadFromLocalStorage()
        if (loaded) {
            resetAnimation()
            appActions.setIsEditable(true)
            appActions.setScenarioMode("custom")
        }
    }

    onMount(async () => {
        // Load map data from service worker cache
        await loadMapData()

        // Initialize user UUID (generates if doesn't exist)
        const userUUID = getUserUUID()

        // Check if URL has a slug (e.g., /abc123def)
        const slug = window.location.pathname.slice(1) // Remove leading /

        // Check if it's a built-in named scenario (e.g., /vantande-gm)
        if (slug && NAMED_SCENARIOS[slug]) {
            if (localStorage.getItem("hasCustomScenario")) {
                // Prompt before overwriting the user's custom scenario
                setPendingNamedScenario(NAMED_SCENARIOS[slug])
                appActions.openModal("saveBeforeLoad")
            } else {
                loadDefaultScenario(scenarioActions, NAMED_SCENARIOS[slug])
                resetAnimation()
                appActions.setIsEditable(false)
                appActions.setMode("simulate")
                appActions.setScenarioMode("default")
            }
        } else if (slug && slug.length > 0) {
            // Load scenario from API
            try {
                const baseUrl = import.meta.env.VITE_BASEURL_API
                const response = await fetch(`${baseUrl}/scenarios/${slug}`)
                if (!response.ok) {
                    const errorBody = await response.text()
                    console.error(`Failed to load scenario "${slug}":`, {
                        status: response.status,
                        statusText: response.statusText,
                        body: errorBody,
                    })
                    throw new Error(`Scenario not found: ${response.statusText}`)
                }
                const project: Project = await response.json()

                // Check if user is the owner
                const isOwner = project.meta.ownerUUID === userUUID

                // If not owner and has localStorage scenario, prompt to save
                if (!isOwner && localStorage.getItem("hasCustomScenario")) {
                    // Store the project to load after user decides
                    setPendingProject(project)
                    appActions.openModal("saveBeforeLoad")
                    return // Wait for user decision
                }

                // Load the shared scenario
                loadScenarioFromProject(scenarioActions, project)

                // Reset clock to scenario start time
                resetAnimation()

                if (isOwner) {
                    // Owner - check if localStorage has a newer version
                    const hasLocal = localStorage.getItem("simulering-project")

                    if (hasLocal) {
                        try {
                            const localProject: Project = JSON.parse(hasLocal)
                            const remoteUpdated = project.meta.updatedAt
                                ? new Date(project.meta.updatedAt).getTime()
                                : 0
                            const localUpdated = localProject.meta.updatedAt
                                ? new Date(localProject.meta.updatedAt).getTime()
                                : 0

                            if (localUpdated > remoteUpdated) {
                                // Local is newer - load from localStorage instead of remote
                                loadScenarioFromProject(scenarioActions, localProject)
                                resetAnimation()
                                appActions.setIsEditable(true)
                                appActions.setScenarioMode("custom")
                                return // Don't load remote version
                            }
                        } catch (err) {
                            console.warn("Failed to compare versions:", err)
                        }
                        // Remote is newer or no local version - load and save
                        localStorage.setItem("hasCustomScenario", "true")
                        scenarioActions.saveToLocalStorage()
                    }
                    appActions.setIsEditable(true)
                } else {
                    // Not owner - read-only until cloned
                    appActions.setIsEditable(false)
                    appActions.setMode("simulate")
                }

                appActions.setScenarioMode("custom")
            } catch (err) {
                console.error(`Failed to load shared scenario from slug "${slug}":`, err)
                if (err instanceof Error) {
                    console.error("Error details:", {
                        message: err.message,
                        stack: err.stack,
                    })
                }

                // Check if user has localStorage data - warn about potential data loss
                const hasLocalData = localStorage.getItem("hasCustomScenario")
                if (hasLocalData) {
                    // Show error and fall back to default
                    appActions.setErrorMessage("Det gick inte att ladda delat scenario. Laddar standard istället.")
                    appActions.openModal("error")

                    // Keep localStorage data but clear URL
                    window.history.replaceState({}, "", "/")
                    const loaded = scenarioActions.loadFromLocalStorage()
                    if (loaded) {
                        resetAnimation()
                        appActions.setIsEditable(true)
                        appActions.setScenarioMode("custom")
                        return
                    }
                } else {
                    appActions.setErrorMessage("Det gick inte att ladda delat scenario. Laddar standard istället.")
                    appActions.openModal("error")
                }

                // Fall back to default
                loadScenarioFromProject(scenarioActions, defaultScenario as any)
                resetAnimation()
                appActions.setScenarioMode("default")
                appActions.setIsEditable(false)
                appActions.setMode("simulate")
                window.history.replaceState({}, "", "/")
            }
        } else {
            // No slug - check localStorage
            const loaded = scenarioActions.loadFromLocalStorage()
            if (!loaded) {
                // No localStorage - use default scenario
                loadScenarioFromProject(scenarioActions, defaultScenario as any)
                resetAnimation()
                appActions.setScenarioMode("default")
                appActions.setIsEditable(false)
                appActions.setMode("simulate")
                // Ensure clean URL
                window.history.replaceState({}, "", "/")
            } else {
                // Has localStorage - custom mode with editing enabled
                resetAnimation()
                appActions.setScenarioMode("custom")
                appActions.setIsEditable(true)
            }
        }

        // Register Service Worker for caching audio/fonts
        registerServiceWorker()
        initPWAInstall()

        // Audio loads on-demand: ambient when playAmbient() is called,
        // timeline cues 30s before they're needed during simulation

        // Detect mobile/tablet viewport
        const checkMobile = () => {
            const width = window.innerWidth
            const shouldBeMobile = width < 768
            appActions.setIsMobile(shouldBeMobile)
        }

        // Initial check
        checkMobile()

        // Also check after a short delay in case viewport isn't ready
        setTimeout(checkMobile, 100)
        setTimeout(checkMobile, 500)

        window.addEventListener("resize", checkMobile)

        // Check if user has seen welcome modal
        const hasSeenWelcome = localStorage.getItem("hasSeenWelcome")
        if (!hasSeenWelcome) {
            appActions.openModal("welcome")
        }

        return () => window.removeEventListener("resize", checkMobile)
    })

    const handleMapClick = (latlng: [number, number], shiftKey: boolean = false) => {
        if (app.mode !== "edit") return

        // Handle viewport pin initial positioning
        if (app.editTab === "viewport") {
            const pinId = app.selectedViewportPinId
            if (pinId) {
                const pin = scenario.viewportPins.find((p) => p.id === pinId)
                // If pin has no position set yet, clicking map sets initial position
                if (pin && pin.position[0] === 0 && pin.position[1] === 0) {
                    scenarioActions.updateViewportPin(pinId, { position: latlng })
                    scenarioActions.scheduleSave()
                    return
                }
            }
        }

        // Handle waypoint adding
        if (!app.isAddingWaypoints) return
        const agentId = app.selectedAgentId
        if (!agentId) return
        scenarioActions.addWaypoint(agentId, latlng, app.activeSegmentIdx)
        scenarioActions.scheduleSave()
    }

    const handleModeChange = (newMode: AppMode) => {
        // On mobile, force simulate mode
        if (app.isMobile && newMode === "edit") {
            return
        }

        appActions.setMode(newMode)
        if (newMode === "simulate") {
            appActions.setIsAddingWaypoints(false)
            appActions.setSimSelectedAgentId(null)
        }
        if (newMode === "edit") {
            appActions.setIsAddingWaypoints(false)
        }
        // Pause animation and clear popups when leaving simulate mode
        if (newMode === "edit") {
            pauseAnimation()
            const popups = document.querySelectorAll(".clock-popup")
            popups.forEach((p) => p.remove())
        }
    }

    const handlePlay = () => {
        appActions.setMode("simulate")
        appActions.setIsAddingWaypoints(false)
        appActions.setSimSelectedAgentId(null)
    }

    const handleReset = () => {
        resetAnimation()
        // Clear viewport pin selection state
        appActions.selectViewportPin(null)
    }

    const handleViewportPinSelect = (pinId: string | null) => {
        appActions.selectViewportPin(pinId)
    }

    const handleScenarioModeChange = (newMode: ScenarioMode, name: string) => {
        appActions.setScenarioMode(newMode)
        appActions.setIsEditable(newMode === "custom") // Editable only in custom mode
        // If switching to default mode, force simulate mode
        if (newMode === "default") {
            appActions.setMode("simulate")
            appActions.setIsAddingWaypoints(false)
        }
    }

    const handleRestartWithTemplate = (templateData: any) => {
        loadDefaultScenario(scenarioActions, templateData)
        handleScenarioModeChange("default", templateData.meta?.name || "Scenario")
        window.history.pushState({}, "", "/")
    }

    return (
        <div class="flex w-screen flex-col bg-gray-100" style={{ height: "100dvh", "max-height": "100dvh" }}>
            <Toolbar
                onModeChange={handleModeChange}
                onOpenMobileMenu={() => handleViewTransition(() => appActions.setIsMobileMenuOpen(true))}
                onScenarioModeChange={handleScenarioModeChange}
            />{" "}
            {/* Mobile Menu */}
            <MobileMenu
                onClose={() => handleViewTransition(() => appActions.setIsMobileMenuOpen(false))}
                onSelectAgent={(agent) => setSelectedAgentForModal(agent)}
                onSelectScenario={handleRestartWithTemplate}
            />
            {/* Agent Details Modal */}
            <AgentDetailsModal agent={selectedAgentForModal()} onClose={() => setSelectedAgentForModal(null)} />
            {/* Mobile Message Banner */}
            <MobileMessageBanner annotation={currentAnnotation()} onDismiss={() => setCurrentAnnotation(null)} />
            <div class="flex flex-1 overflow-hidden">
                <div class="relative flex-1 overflow-hidden">
                    <MapView
                        onMapClick={handleMapClick}
                        onVisibleAgentsChange={setVisibleAgentIds}
                        onClockAnnotation={(annotation) => {
                            if (app.isMobile) {
                                setCurrentAnnotation(annotation)
                            }
                        }}
                        onAgentClick={(agentId) => {
                            if (app.mode === "simulate") {
                                if (app.isMobile) {
                                    // On mobile, show agent details modal directly
                                    const agent = scenario.agents.find((a) => a.id === agentId)
                                    if (agent) setSelectedAgentForModal(agent)
                                } else {
                                    appActions.setSimSelectedAgentId(agentId)
                                }
                            }
                        }}
                        onEndTimeReached={() => {
                            // Open analysis modal when endTime is reached (if showAnalysisAtEnd is enabled)
                            handleViewTransition(() => appActions.openModal("analysis"))
                        }}
                    />{" "}
                    {app.mode === "edit" && app.isAddingWaypoints && app.selectedAgentId && (
                        <div class="pointer-events-none absolute top-3 left-1/2 z-[1000] -translate-x-1/2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-lg">
                            Klicka på kartan för att lägga till vägpunkter (Segment {app.activeSegmentIdx + 1})
                        </div>
                    )}
                    {/* C. Simulation overlay - desktop only */}
                    {app.mode === "simulate" && !app.isMobile && (
                        <SimulationOverlay
                            visibleAgentIds={visibleAgentIds()}
                        />
                    )}
                </div>
                {/* H. Hide sidebar in simulate mode, on mobile, or in default mode */}
                {!app.isMobile && app.mode === "edit" && app.scenarioMode === "custom" && (
                    <div class="flex h-full w-96 flex-col overflow-hidden border-l border-gray-200 bg-gray-50">
                        {/* Tab Headers */}
                        <div class="flex border-b border-gray-200 bg-white">
                            <button
                                class={`flex flex-1 items-center justify-center px-2 py-2.5 text-lg transition-colors ${
                                    app.editTab === "agents"
                                        ? "border-b-2 border-blue-600 bg-blue-50 text-blue-700"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                                onClick={() => handleEditTabChange("agents")}
                                title="Aktörer"
                            >
                                <div class="" style="transform: scale(0.8);" innerHTML={actorIcon} />
                            </button>
                            <button
                                class={`flex flex-1 items-center justify-center px-2 py-2.5 text-lg transition-colors ${
                                    app.editTab === "events"
                                        ? "border-b-2 border-blue-600 bg-blue-50 text-blue-700"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                                onClick={() => handleEditTabChange("events")}
                                title="Händelser"
                            >
                                <div class="" style="transform: scale(0.8);" innerHTML={eventIcon} />
                            </button>
                            <button
                                class={`flex flex-1 items-center justify-center px-2 py-2.5 text-lg transition-colors ${
                                    app.editTab === "speed"
                                        ? "border-b-2 border-blue-600 bg-blue-50 text-blue-700"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                                onClick={() => handleEditTabChange("speed")}
                                title="Hastighet"
                            >
                                <div class="" style="transform: scale(0.8);" innerHTML={speedIcon} />
                            </button>
                            <button
                                class={`flex flex-1 items-center justify-center px-2 py-2.5 text-lg transition-colors ${
                                    app.editTab === "analysis"
                                        ? "border-b-2 border-blue-600 bg-blue-50 text-blue-700"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                                onClick={() => handleEditTabChange("analysis")}
                                title="Analys"
                            >
                                <div class="" style="transform: scale(0.8);" innerHTML={analysisIcon} />
                            </button>
                            <button
                                class={`flex flex-1 items-center justify-center px-2 py-2.5 text-lg transition-colors ${
                                    app.editTab === "viewport"
                                        ? "border-b-2 border-blue-600 bg-blue-50 text-blue-700"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                                onClick={() => handleEditTabChange("viewport")}
                                title="Kamerapositioner"
                            >
                                <div class="" style="transform: scale(0.8);" innerHTML={pinIcon} />
                            </button>
                            <button
                                class={`flex flex-1 items-center justify-center px-2 py-2.5 text-lg transition-colors ${
                                    app.editTab === "settings"
                                        ? "border-b-2 border-blue-600 bg-blue-50 text-blue-700"
                                        : "text-gray-600 hover:bg-gray-50"
                                }`}
                                onClick={() => handleEditTabChange("settings")}
                                title="Inställningar"
                            >
                                <div class="" style="transform: scale(0.8);" innerHTML={settingsIcon} />
                            </button>
                        </div>{" "}
                        {/* Tab Content */}
                        {app.editTab === "agents" ? (
                            <AgentPanel />
                        ) : app.editTab === "events" ? (
                            <EventsPanel />
                        ) : app.editTab === "speed" ? (
                            <SpeedPanel />
                        ) : app.editTab === "viewport" ? (
                            <ViewportPanel
                                selectedPinId={app.selectedViewportPinId}
                                onSelectPin={handleViewportPinSelect}
                            />
                        ) : app.editTab === "analysis" ? (
                            <AnalysisPanel />
                        ) : (
                            <SettingsPanel />
                        )}
                    </div>
                )}
            </div>
            <ClockControls
                onPlay={handlePlay}
                onReset={handleReset}
            />
            {/* Help button - fixed in bottom right corner, outside main container */}
            <button
                class="right-3 bottom-20 z-20 hidden h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700 shadow-sm transition-colors hover:bg-gray-200 md:fixed md:right-8 md:bottom-20 md:flex"
                onClick={() => handleViewTransition(() => appActions.openModal("help"))}
                title="Hjälp"
            >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                </svg>
            </button>
            {/* Help Modal */}
            <HelpModal isOpen={app.modals.help} onClose={() => handleViewTransition(() => appActions.closeModal("help"))} />
            {/* Save Before Load Modal */}
            <SaveBeforeLoadModal
                isOpen={app.modals.saveBeforeLoad}
                onSave={handleSaveAndLoad}
                onDiscard={handleDiscardAndLoad}
                onCancel={handleCancelLoad}
            />
            {/* Share Result Modal */}
            <ShareResultModal
                isOpen={app.modals.shareResult}
                onClose={() => handleViewTransition(() => appActions.closeModal("shareResult"))}
                url={app.shareUrl}
            />
            {/* Welcome Modal */}
            <WelcomeModal
                isOpen={app.modals.welcome}
                onClose={() =>
                    handleViewTransition(() => {
                        localStorage.setItem("hasSeenWelcome", "true")
                        appActions.closeModal("welcome")
                    })
                }
            />
            {/* Analysis Modal */}
            <Show when={app.modals.analysis}>
                <AnalysisModal onClose={() => handleViewTransition(() => appActions.closeModal("analysis"))} />
            </Show>
            {/* Error Modal */}
            <SaveBeforeLoadModal
                isOpen={app.modals.error}
                onCancel={() => handleViewTransition(() => appActions.closeModal("error"))}
                simpleMode={true}
                title="Fel vid laddning"
                message={app.errorMessage}
            />
            {/* Cookie Consent */}
            <CookieConsent />
            {/* Restart Modal (mobile) */}
            <RestartModal
                isOpen={restartModalOpen()}
                onClose={() => setRestartModalOpen(false)}
                onSelect={handleRestartWithTemplate}
            />
        </div>
    )
}
export default App
